import { NgIf } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { GeoJSONSourceComponent, LayerComponent, MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { Gateway } from '@wheere/api-site';

@Component({
  selector: 'lib-map-gateway-marker',
  standalone: true,
  imports: [MarkerComponent, PopupComponent, NgIf, FastSvgComponent, GeoJSONSourceComponent, LayerComponent],
  template: `
    @if (gateway().longitude && gateway().latitude && pseudo() && color() && show()) {
      <mgl-geojson-source
        [id]="'gateway-circle-' + gateway().id"
        [data]="getGatewayCircleData(gateway(), pseudo()!)"></mgl-geojson-source>
      <mgl-layer
        [id]="'gateway-circle-fill-' + gateway().id"
        [source]="'gateway-circle-' + gateway().id"
        type="fill"
        [paint]="{
          'fill-color': color(),
          'fill-opacity': 0.05,
        }">
      </mgl-layer>
      <mgl-layer
        [id]="'gateway-circle-line-' + gateway().id"
        [source]="'gateway-circle-' + gateway().id"
        type="line"
        [paint]="{
          'line-color': this.color(),
          'line-width': 2,
          'line-opacity': 0.8,
        }">
      </mgl-layer>
    }
    <ng-container *ngIf="hasPosition()">
      <mgl-marker #markerRef [lngLat]="[gateway().longitude!, gateway().latitude!]">
        <div class="relative flex flex-col items-center">
          @if (gwPower() !== undefined && gwPower() !== null) {
            <div class="mb-1 rounded bg-gray-800 px-2 py-0.5 text-xs font-semibold text-white shadow-md">
              {{ gwPower() }} dBm
              @if (snr() !== undefined && snr() !== null) {
                / {{ snr() }} dB
              }
            </div>
          }
          @if (gateway().orderSite === 0) {
            <button class="relative flex size-10" [style.color]="color() ? color() : '#ED107D'">
              <fast-svg name="gateway-ref" size="100%"></fast-svg>
            </button>
          } @else {
            <button class="relative flex size-6" [style.color]="color() ? color() : '#ED107D'" [style.opacity]="1">
              <fast-svg name="gateway" size="100%"></fast-svg>
            </button>
          }
        </div>
      </mgl-marker>

      <mgl-popup [offset]="16" [marker]="markerRef" [closeButton]="false" [maxWidth]="'250'">
        <h4 class="text-lg font-semibold">{{ gateway().name }} - Order {{ gateway().orderSite ?? 0 }}</h4>
        <div class="flex items-center gap-2">
          <i class="pi pi-map-marker text-primary-500"></i>
          <span class="text-sm">
            <span class="font-bold">Lat:</span> {{ gateway().latitude?.toFixed(8) }},
            <span class="font-bold">Lng:</span> {{ gateway().longitude?.toFixed(8) }} <span class="font-bold">Alt:</span
            >{{ gateway().altitude?.toFixed(8) }}
          </span>
        </div>
      </mgl-popup>
    </ng-container>
  `,
})
export class MapGatewayMarkerComponent {
  gateway = input.required<Gateway>();
  color = input<string>();
  pseudo = input<number>();
  gwPower = input<number>();
  snr = input<number>();
  show = input<boolean>(false);

  /** A marker is placeable only with real numeric coordinates. {@code typeof === 'number'}
   * excludes both {@code null} and {@code undefined} while keeping a valid 0 (equator / prime
   * meridian) — unlike a truthy check. */
  protected readonly hasPosition = computed(
    () => typeof this.gateway().longitude === 'number' && typeof this.gateway().latitude === 'number'
  );

  getGatewayCircleData(gateway: Gateway, radiusInMeters: number): GeoJSON.FeatureCollection {
    if (!gateway.longitude || !gateway.latitude || !radiusInMeters) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    const center = [Number(gateway.longitude), Number(gateway.latitude)];
    const points = 64; // Number of points to create the circle
    const coordinates: number[][] = [];

    for (let i = 0; i <= points; i++) {
      const angle = (i * 360) / points;
      const point = this.calculatePointAtDistance(center[1], center[0], radiusInMeters, angle);
      coordinates.push([point.lng, point.lat]);
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {
            gatewayId: gateway.id,
            range: radiusInMeters,
          },
        },
      ],
    };
  }

  private calculatePointAtDistance(
    lat: number,
    lng: number,
    distance: number,
    bearing: number
  ): { lat: number; lng: number } {
    const R = 6371000;
    const bearingRad = this.toRadians(bearing);
    const latRad = this.toRadians(lat);
    const lngRad = this.toRadians(lng);

    const newLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(distance / R) + Math.cos(latRad) * Math.sin(distance / R) * Math.cos(bearingRad)
    );

    const newLngRad =
      lngRad +
      Math.atan2(
        Math.sin(bearingRad) * Math.sin(distance / R) * Math.cos(latRad),
        Math.cos(distance / R) - Math.sin(latRad) * Math.sin(newLatRad)
      );

    return {
      lat: newLatRad * (180 / Math.PI),
      lng: newLngRad * (180 / Math.PI),
    };
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}
